import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePartnerHomeCustomerStore } from "../../../../stores/Cac/Partner/partnerHomeCustomer";
import useSorting from "../../../../hooks/useSorting";
import Pen from "../../../../assets/icons/edit.svg";
import Trash from "../../../../assets/icons/trash.svg";
import { useTokenContext } from "../../../../context/tokenContext";
import { useState } from "react";
import SearchBar from "../../../Admin/Services/SearchBar";
import Return from "../../../../assets/icons/return.svg";
import Bell from "../../../../assets/icons/bellBlackBold.svg";

const PartnerCustomerTable = () => {
  const { t } = useTranslation();
  // Context
  const { roleId, isSuperAdmin } = useTokenContext();
  // Store
  const { clients, setChoiceModal, setIdCustomer } =
    usePartnerHomeCustomerStore();
  const navigate = useNavigate();
  // Hooks
  const { idPartner } = useParams();
  const [sortedData, handleSort] = useSorting(clients);
  
  // State pour le tri
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Fonction de tri personnalisée
  const handleCustomSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    handleSort(field);
  };

  // Fonction pour obtenir l'icône de tri
  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    if (sortDirection === "asc") {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    } else {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      );
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {/* Header avec système de tri moderne */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-3 py-3 sm:px-5 2xl:py-4">
        <div className="flex flex-col items-start justify-between gap-3 lg:flex-row lg:items-center">
        <div className="hidden md:flex w-full items-center gap-3 sm:gap-4 lg:w-auto">
          <button
            type="button"
            className="group flex-shrink-0 rounded-xl bg-gray-100 p-2.5 transition-colors duration-200 hover:bg-gray-200 sm:p-3"
            onClick={() =>
              isSuperAdmin === 1
                ? navigate("/superadmin/dashboard")
                : navigate("/dashboard")
            }
            title={t("common.back")}
          >
            <img src={Return} alt="return" className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
          </button>
          <div className="flex min-w-0 flex-1 lg:w-72 lg:flex-none">
            <div className="relative w-full">
              <SearchBar />
            </div>
          </div>
        </div>
          
          {/* Filtres et tri */}
          <div className="hidden md:flex w-full items-center gap-2 overflow-x-auto pb-1 lg:w-auto lg:pb-0">
            <span className="flex-shrink-0 text-sm font-medium text-gray-600">{t("common.sortBy")}</span>
            
            {/* Bouton de tri par nom */}
            <button 
              onClick={() => handleCustomSort("name")}
              className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                sortField === "name" 
                  ? "bg-blue-100 text-blue-700 border border-blue-200" 
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
              }`}
            >
              <span>{t("partner.customerName")}</span>
              {getSortIcon("name")}
            </button>
            
            {/* Bouton de tri par créateur */}
            <button 
              onClick={() => handleCustomSort("fullName")}
              className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                sortField === "fullName" 
                  ? "bg-blue-100 text-blue-700 border border-blue-200" 
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
              }`}
            >
              <span>{t("common.creator")}</span>
              {getSortIcon("fullName")}
            </button>
            
            {/* Bouton de tri par missions */}
            <button 
              onClick={() => handleCustomSort("missionCount")}
              className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                sortField === "missionCount" 
                  ? "bg-blue-100 text-blue-700 border border-blue-200" 
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
              }`}
            >
              <span>{t("partner.missions")}</span>
              {getSortIcon("missionCount")}
            </button>
          </div>
        </div>
      </div>

      {/* Table des clients */}
      <div className="min-h-0 w-full flex-1 scroll-pb-20 overflow-y-auto pb-20 sm:scroll-pb-3 sm:pb-3">
        {/* En-têtes de colonnes */}
        <div
          className={
            roleId === 2
              ? "sticky top-0 z-10 hidden w-full grid-cols-4 border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700 md:grid"
              : "sticky top-0 z-10 hidden w-full grid-cols-5 border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700 md:grid"
          }
        >
          <div className="col-start-1 col-end-3 flex items-center">
            {t("partner.customerName")}
          </div>
          <div className="col-start-3 col-end-4 flex items-center">
            {t("common.creator")}
          </div>
          <div className="hidden sm:col-start-4 col-end-5 sm:flex items-center">
            {t("partner.missions")}
          </div>
          {roleId !== 2 && (
            <div className="hidden md:col-start-5 md:col-end-6 md:flex items-center justify-center">
              Actions
            </div>
          )}
        </div>

        {/* Corps de la table */}
        <div className="w-full">
          {sortedData?.length === 0 || sortedData === undefined ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t("partner.noCustomerTitle")}</h3>
              <p className="text-gray-500">{t("partner.noCustomerText")}</p>
            </div>
          ) : (
            sortedData?.map((client, index) => (
              <div
                key={client?.id}
                className={`w-full border-b border-gray-100 transition-all duration-200 hover:bg-blue-50 ${
                  roleId === 2
                    ? "flex flex-col md:grid md:grid-cols-4"
                    : "flex flex-col md:grid md:grid-cols-5"
                }`}
              >
                <button
                  type="button"
                  className="col-start-1 col-end-5 flex w-full cursor-pointer flex-col gap-2 p-4 text-left transition-colors duration-200 hover:bg-blue-50 md:grid md:grid-cols-4 md:gap-0"
                  onClick={() => {
                    navigate(
                      `/partner/${idPartner}/customers/${client?.id}/missions`
                    );
                  }}
                >
                  <div className="flex w-full items-center justify-start md:col-start-1 md:col-end-3">
                    <div className="font-medium text-gray-900">{client?.name}</div>
                  </div>
                  <div className="flex w-full items-center justify-start md:col-start-3 md:col-end-4">
                    <div className="text-gray-600 text-sm">{client?.User?.fullName}</div>
                  </div>
                  <div className="hidden md:flex col-start-4 col-end-5 w-full justify-start items-center">
                    <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 relative">
                      {client?.priority === 1 ? <img src={Bell} alt="" className="w-4 h-4 absolute -left-2 -top-2" /> : null}
                      {client?.missionCount || 0} mission{client?.missionCount > 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="flex items-center md:hidden">
                    <div className="relative inline-flex items-center rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-800">
                      {client?.priority === 1 ? <img src={Bell} alt="" className="absolute -left-2 -top-2 h-4 w-4" /> : null}
                      {client?.missionCount || 0} mission{client?.missionCount > 1 ? "s" : ""}
                    </div>
                  </div>
                </button>
                
                <div
                  className={
                    roleId === 2
                      ? "hidden"
                      : "hidden md:flex items-center justify-end border-t border-gray-100 px-4 pb-3 pt-2 md:col-start-5 md:col-end-6 md:justify-around md:border-t-0 md:p-4"
                  }
                >
                  <div className="grid w-full max-w-28 grid-cols-2 gap-2">
                    <button
                      type="button"
                      className="col-start-1 col-end-2 flex items-center justify-center p-2 bg-gray-100 hover:bg-blue-100 rounded-lg transition-colors duration-200 cursor-pointer"
                      onClick={() => {
                        setIdCustomer(client?.id);
                        setChoiceModal("update");
                      }}
                      title="Modifier"
                    >
                      <img
                        className="w-4 h-4"
                        src={Pen}
                        alt="Modifier"
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIdCustomer(client?.id);
                        setChoiceModal("delete");
                      }}
                      className="col-start-2 col-end-3 flex items-center justify-center p-2 bg-gray-100 hover:bg-red-100 rounded-lg transition-colors duration-200 cursor-pointer"
                      title={t("common.delete")}
                    >
                      <img
                        className="w-4 h-4"
                        src={Trash}
                        alt="Supprimer"
                      />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerCustomerTable;
